import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useLogoutMutation } from '@/modules/auth/mutations/useLogoutMutation';
import { roles } from '@/modules/auth/roles';
import { hasAnyRole } from '@/modules/auth/utils/hasRoles';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import {
  getHMTenantSelection,
  TENANT_QUERY_PARAM,
} from '@/modules/hiring-manager/utils/tenant';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { actionErrorMessage } from '@/utils/actionErrorMessage';
import { getInitials } from '@/utils/getInitials';

/**
 * Shared portal layout state: tenant selection and its URL sync,
 * sign-up completion, and sidebar actions.
 */
export const usePortalLayoutState = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousTenantIdRef = useRef<string | undefined>(undefined);

  const currentUserQuery = useCurrentUserQuery();
  const currentTenantQuery = useCurrentTenantQuery();
  const logoutMutation = useLogoutMutation();

  const tenantSelection = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );

  const companiesQuery = useCompaniesQuery({
    enabled: tenantSelection.canSelectTenant,
  });

  const signedInUser = currentUserQuery.data;
  const userRoles = signedInUser?.roles ?? [];

  const availableCompanies = (companiesQuery.data ?? []).filter(
    (company) =>
      tenantSelection.tenantIds.length === 0 ||
      tenantSelection.tenantIds.includes(company.id),
  );

  // Staff can browse any tenant, so the current-tenant query (which follows
  // the user's default tenant) is only right when no selection is possible.
  const selectedTenantName = tenantSelection.canSelectTenant
    ? availableCompanies.find(
        (company) => company.id === tenantSelection.selectedTenantId,
      )?.name
    : currentTenantQuery.data?.name;

  useEffect(() => {
    if (!tenantSelection.canSelectTenant) {
      if (tenantSelection.tenantParam) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(TENANT_QUERY_PARAM);
        setSearchParams(nextParams, { replace: true });
      }
      return;
    }

    if (
      tenantSelection.selectedTenantId &&
      tenantSelection.tenantParam !== tenantSelection.selectedTenantId
    ) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(TENANT_QUERY_PARAM, tenantSelection.selectedTenantId);
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    tenantSelection.canSelectTenant,
    tenantSelection.selectedTenantId,
    tenantSelection.tenantParam,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!tenantSelection.canSelectTenant || !tenantSelection.selectedTenantId) {
      return;
    }

    const previousTenantId = previousTenantIdRef.current;
    if (
      previousTenantId &&
      previousTenantId !== tenantSelection.selectedTenantId
    ) {
      queryClient.invalidateQueries();
    }
    previousTenantIdRef.current = tenantSelection.selectedTenantId;
  }, [
    tenantSelection.canSelectTenant,
    tenantSelection.selectedTenantId,
    queryClient,
  ]);

  const changeTenant = (tenantId: string) => {
    if (!tenantId || tenantId === tenantSelection.selectedTenantId) {
      return;
    }

    const nextSearchParams = new URLSearchParams();
    nextSearchParams.set(TENANT_QUERY_PARAM, tenantId);
    navigate({ pathname: '/hm', search: nextSearchParams.toString() });
  };

  const logout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate('/login', { replace: true });
      },
      onError: (error) =>
        toast.error(actionErrorMessage('sign you out', error)),
    });
  };

  return {
    availableCompanies,
    canReturnToRecruiterDashboard: hasAnyRole(
      [roles.Recruiter, roles.Admin, roles.SuperAdmin],
      userRoles,
    ),
    changeTenant,
    hasFinishedSignupFlow:
      currentTenantQuery.data?.onboarding?.isComplete === true,
    hasCoordinationEnabledJobs:
      currentTenantQuery.data?.hasCoordinationEnabledJobs === true,
    initials: signedInUser
      ? getInitials(
          `${signedInUser.firstName ?? ''} ${signedInUser.lastName ?? ''}`,
        )
      : '',
    isLoadingCompanies: companiesQuery.isLoading,
    logout,
    selectedTenantName,
    signedInUser,
    tenantSelection,
  };
};
